// Pattern Recognition Engine - ML-inspired pattern detection
// Identifies technical patterns in price movements

export class PatternRecognition {
  constructor(data) {
    this.data = data;
  }

  /**
   * Detect all patterns in the data
   */
  detectAllPatterns() {
    return {
      consecutiveMovements: this.detectConsecutiveMovements(),
      supportResistance: this.detectSupportResistance(),
      volatilityClusters: this.detectVolatilityClusters(),
      trendStrength: this.detectTrendStrength(),
      meanReversion: this.detectMeanReversion(),
      breakouts: this.detectBreakouts(),
    };
  }

  /**
   * Detect consecutive movements (streaks)
   */
  detectConsecutiveMovements() {
    const patterns = [];
    let currentStreak = { direction: null, count: 0, startIndex: 0 };

    for (let i = 0; i < this.data.length - 1; i++) {
      const change = this.data[i].price - this.data[i + 1].price;
      const direction = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';

      if (direction === currentStreak.direction) {
        currentStreak.count++;
      } else {
        if (currentStreak.count >= 3) {
          patterns.push({
            type: 'consecutive',
            direction: currentStreak.direction,
            count: currentStreak.count,
            startDate: this.data[currentStreak.startIndex].date,
            endDate: this.data[i].date,
            significance: currentStreak.count >= 5 ? 'high' : 'medium',
          });
        }
        currentStreak = { direction, count: 1, startIndex: i };
      }
    }

    return patterns;
  }

  /**
   * Detect support and resistance levels
   */
  detectSupportResistance() {
    const prices = this.data.map(d => d.price);
    const levels = [];

    // Find local minima (support) and maxima (resistance)
    for (let i = 5; i < prices.length - 5; i++) {
      const window = prices.slice(i - 5, i + 6);
      const current = prices[i];

      // Support: local minimum
      if (current === Math.min(...window)) {
        const touchCount = prices.filter(p => Math.abs(p - current) < current * 0.005).length;
        if (touchCount >= 3) {
          levels.push({
            type: 'support',
            level: current,
            touches: touchCount,
            strength: touchCount >= 5 ? 'strong' : 'moderate',
            date: this.data[i].date,
          });
        }
      }

      // Resistance: local maximum
      if (current === Math.max(...window)) {
        const touchCount = prices.filter(p => Math.abs(p - current) < current * 0.005).length;
        if (touchCount >= 3) {
          levels.push({
            type: 'resistance',
            level: current,
            touches: touchCount,
            strength: touchCount >= 5 ? 'strong' : 'moderate',
            date: this.data[i].date,
          });
        }
      }
    }

    return levels;
  }

  /**
   * Detect volatility clusters (GARCH-like)
   */
  detectVolatilityClusters() {
    const changes = [];
    for (let i = 0; i < this.data.length - 1; i++) {
      const change = Math.abs((this.data[i].price - this.data[i + 1].price) / this.data[i + 1].price * 100);
      changes.push({ date: this.data[i].date, volatility: change });
    }

    const avgVol = changes.reduce((sum, c) => sum + c.volatility, 0) / changes.length;
    const stdVol = Math.sqrt(
      changes.reduce((sum, c) => sum + Math.pow(c.volatility - avgVol, 2), 0) / changes.length
    );

    const clusters = [];
    let inCluster = false;
    let clusterStart = null;
    let clusterCount = 0;

    changes.forEach((c, i) => {
      if (c.volatility > avgVol + stdVol) {
        if (!inCluster) {
          inCluster = true;
          clusterStart = i;
          clusterCount = 1;
        } else {
          clusterCount++;
        }
      } else {
        if (inCluster && clusterCount >= 3) {
          clusters.push({
            type: 'volatility_cluster',
            startDate: changes[clusterStart].date,
            endDate: changes[i - 1].date,
            duration: clusterCount,
            avgVolatility: changes.slice(clusterStart, i).reduce((sum, c) => sum + c.volatility, 0) / clusterCount,
          });
        }
        inCluster = false;
      }
    });

    return clusters;
  }

  /**
   * Detect trend strength (ADX-inspired)
   */
  detectTrendStrength() {
    const window = 14; // periods
    const trends = [];

    for (let i = window; i < this.data.length; i++) {
      const slice = this.data.slice(i - window, i);
      
      let upMoves = 0;
      let downMoves = 0;
      
      for (let j = 0; j < slice.length - 1; j++) {
        const change = slice[j].price - slice[j + 1].price;
        if (change > 0) upMoves++;
        else if (change < 0) downMoves++;
      }

      const trendStrength = Math.abs(upMoves - downMoves) / window;
      const direction = upMoves > downMoves ? 'bullish' : 'bearish';

      if (trendStrength > 0.5) {
        trends.push({
          type: 'strong_trend',
          direction,
          strength: trendStrength.toFixed(2),
          date: this.data[i].date,
          confidence: trendStrength > 0.7 ? 'high' : 'medium',
        });
      }
    }

    return trends;
  }

  /**
   * Detect mean reversion opportunities
   */
  detectMeanReversion() {
    const window = 20;
    const opportunities = [];

    for (let i = window; i < this.data.length; i++) {
      const slice = this.data.slice(i - window, i);
      const mean = slice.reduce((sum, d) => sum + d.price, 0) / window;
      const stdDev = Math.sqrt(
        slice.reduce((sum, d) => sum + Math.pow(d.price - mean, 2), 0) / window
      );

      const current = this.data[i].price;
      const zScore = (current - mean) / stdDev;

      if (Math.abs(zScore) > 2) {
        opportunities.push({
          type: 'mean_reversion',
          direction: zScore > 0 ? 'overbought' : 'oversold',
          zScore: zScore.toFixed(2),
          currentPrice: current,
          mean: mean.toFixed(2),
          date: this.data[i].date,
          signal: Math.abs(zScore) > 2.5 ? 'strong' : 'moderate',
        });
      }
    }

    return opportunities;
  }

  /**
   * Detect breakout patterns
   */
  detectBreakouts() {
    const breakouts = [];
    const lookback = 20;

    for (let i = lookback; i < this.data.length; i++) {
      const slice = this.data.slice(i - lookback, i);
      const recentHigh = Math.max(...slice.map(d => d.price));
      const recentLow = Math.min(...slice.map(d => d.price));
      const range = recentHigh - recentLow;

      const current = this.data[i].price;

      // Upside breakout
      if (current > recentHigh && range > recentHigh * 0.02) {
        breakouts.push({
          type: 'breakout',
          direction: 'upside',
          breakoutLevel: recentHigh.toFixed(2),
          currentPrice: current.toFixed(2),
          magnitude: ((current - recentHigh) / recentHigh * 100).toFixed(2) + '%',
          date: this.data[i].date,
        });
      }

      // Downside breakout
      if (current < recentLow && range > recentLow * 0.02) {
        breakouts.push({
          type: 'breakout',
          direction: 'downside',
          breakoutLevel: recentLow.toFixed(2),
          currentPrice: current.toFixed(2),
          magnitude: ((recentLow - current) / recentLow * 100).toFixed(2) + '%',
          date: this.data[i].date,
        });
      }
    }

    return breakouts;
  }

  /**
   * Generate ML-style prediction based on patterns
   */
  generatePrediction(recentPatterns) {
    let bullishScore = 0;
    let bearishScore = 0;
    const signals = [];

    // Score consecutive movements
    recentPatterns.consecutiveMovements.forEach(p => {
      if (p.direction === 'up') {
        bullishScore += p.count * 0.5;
        signals.push(`${p.count} תנועות עולות רצופות`);
      } else {
        bearishScore += p.count * 0.5;
        signals.push(`${p.count} תנועות יורדות רצופות`);
      }
    });

    // Score mean reversion
    recentPatterns.meanReversion.forEach(p => {
      if (p.direction === 'oversold') {
        bullishScore += Math.abs(parseFloat(p.zScore)) * 0.3;
        signals.push(`מצב oversold (${p.zScore})`);
      } else {
        bearishScore += Math.abs(parseFloat(p.zScore)) * 0.3;
        signals.push(`מצב overbought (${p.zScore})`);
      }
    });

    // Score trend strength
    recentPatterns.trendStrength.forEach(p => {
      const score = parseFloat(p.strength) * 2;
      if (p.direction === 'bullish') {
        bullishScore += score;
        signals.push(`מגמה עולה חזקה (${p.strength})`);
      } else {
        bearishScore += score;
        signals.push(`מגמה יורדת חזקה (${p.strength})`);
      }
    });

    const totalScore = bullishScore + bearishScore;
    const bullishProb = totalScore > 0 ? (bullishScore / totalScore * 100) : 50;

    return {
      direction: bullishScore > bearishScore ? 'עולה' : bearishScore > bullishScore ? 'יורד' : 'צדדי',
      confidence: Math.abs(bullishScore - bearishScore) / Math.max(bullishScore, bearishScore, 1),
      bullishProbability: bullishProb.toFixed(0),
      bearishProbability: (100 - bullishProb).toFixed(0),
      signals,
    };
  }
}

export default PatternRecognition;
